/**
 * How long the DB Manager may sit in the background before its open database
 * connections are closed.
 *
 * Every extension panel is torn down after DEFAULT_IDLE_TEARDOWN_MS (60 s) of
 * being hidden, and the teardown closes every DB connection it opened
 * (ExtensionHost → DB_DISCONNECT_ALL_COMMANDS). For the DB Manager that meant a
 * minute in another panel lost the connection. Owner, 30.09.2026: dropping
 * connections is fine after a long break (30 min – 1 h), not after a minute —
 * and it must be configurable, including "never".
 *
 * The setting lives in the app preferences (`dbIdleDisconnect`) and is shown
 * under Settings → Extensions → DB Manager. Other extensions keep the 60 s
 * default: they hold no server connection worth keeping.
 */
import { DEFAULT_IDLE_TEARDOWN_MS } from './sdk/protocol';

export const DB_MANAGER_EXTENSION_ID = 'com.apiboo.db-manager';

const MINUTE = 60 * 1000;

/** Stored value → milliseconds (null = never). Order is the settings menu order. */
export const DB_IDLE_DISCONNECT_OPTIONS = [
  { value: '15m', ms: 15 * MINUTE },
  { value: '30m', ms: 30 * MINUTE },
  { value: '1h', ms: 60 * MINUTE },
  { value: '2h', ms: 120 * MINUTE },
  { value: 'never', ms: null },
];

export const DB_IDLE_DISCONNECT_DEFAULT = '30m';

/** A stored value, or the default for anything unknown (old installs, typos, tampering). */
export function normalizeDbIdleDisconnect(raw) {
  return DB_IDLE_DISCONNECT_OPTIONS.some((o) => o.value === raw) ? raw : DB_IDLE_DISCONNECT_DEFAULT;
}

/**
 * The hidden-panel grace period for one extension, in ms — or null for "never
 * tear it down while hidden" (closing the app still disconnects everything).
 */
export function idleTeardownMsFor(extensionId, dbIdleDisconnect) {
  if (extensionId !== DB_MANAGER_EXTENSION_ID) return DEFAULT_IDLE_TEARDOWN_MS;
  const value = normalizeDbIdleDisconnect(dbIdleDisconnect);
  return DB_IDLE_DISCONNECT_OPTIONS.find((o) => o.value === value).ms;
}
